import jsPDF from 'jspdf';
import { M, CONTENT_W, procesarFecha, generarNroDocumento } from './pdf/theme.js';
import { dibujarFooter } from './pdf/layout.js';
import { cargarFoto, resetFotosConError } from './pdf/helpers.js';
import { cabecera, espacio, datos, tabla, parrafo, texto, rotulo, plata, linea, F, K } from './pdf/v2/base.js';
import { guardarPDF } from './pdf/entregar.js';

/**
 * generarPDFCotizacion
 * Cotización de precios escalonados por cantidad — uno o varios productos.
 * 27-oct-2026: mismo diseño limpio que el presupuesto y la venta (v2): logo + título a la derecha,
 * cliente en columnas, foto grande del repuesto, tabla "1 unidad / Desde N unidades" y el mejor
 * precio destacado en el recuadro gris con la barra roja (igual que el total de los otros PDF).
 */
export async function generarPDFCotizacion({
    clienteNombre       = '',
    clienteTelefono     = '',
    productos           = [],
    // Legacy (producto unico)
    productoNombre,
    productoCodigo,
    productoDescripcion,
    fotoUrl,
    filas: filasLegacy,
    validezDias         = '7',
    notas               = '',
}) {
    if (productos.length === 0 && productoNombre) {
        productos = [{
            productoNombre,
            productoCodigo:      productoCodigo || '',
            productoDescripcion: productoDescripcion || '',
            fotoUrl:             fotoUrl || '',
            filas:               filasLegacy || [],
        }];
    }

    resetFotosConError();
    const multi  = productos.length > 1;
    const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW  = doc.internal.pageSize.getWidth();
    const hoy    = new Date();
    const fecha  = procesarFecha(hoy.toISOString());
    const nroDoc = generarNroDocumento('CT', fecha, clienteNombre || 'CLI');
    const dias   = Math.max(1, parseInt(validezDias, 10) || 7);
    const vence  = new Date(hoy.getTime() + dias * 86400000).toLocaleDateString('es-AR');

    const fotos = await Promise.all(productos.map(p => cargarFoto(p.fotoUrl || null, 700, 0.85).catch(() => null)));

    // ── Cabecera ────────────────────────────────────────────────────────────
    const ctx = {
        titulo: 'Cotización por cantidad',
        sub: [`N° ${nroDoc}`, fecha, `Válida hasta ${vence}`].join('  ·  '),
    };
    let y = cabecera(doc, ctx);

    // ── Cliente | Condiciones ───────────────────────────────────────────────
    const notasCortas = String(notas || '').trim();
    const notasEnColumna = notasCortas && notasCortas.length <= 120;
    y = datos(doc, y,
        { rotulo: 'Cliente', nombre: clienteNombre || 'Cliente', lineas: [clienteTelefono ? `Tel. ${clienteTelefono}` : null] },
        { rotulo: 'Condiciones', lineas: [notasEnColumna ? notasCortas : null, `Precios válidos por ${dias} día${dias === 1 ? '' : 's'}`, 'Sujeto a disponibilidad de stock'] },
    );
    linea(doc, y - 1);
    y += 6;

    // ── Productos ───────────────────────────────────────────────────────────
    const FOTO = multi ? 30 : 44;
    for (let pi = 0; pi < productos.length; pi++) {
        const prod  = productos[pi];
        const foto  = fotos[pi];
        const filas = [...(prod.filas || [])]
            .map(f => ({ cant: Number(f.cantidad) || 0, unit: Number(f.precioUnitario) || 0 }))
            .filter(f => f.cant > 0 && f.unit > 0)
            .sort((a, b) => a.cant - b.cant);
        if (filas.length === 0) continue;

        // Que el producto entero (foto + tabla + recuadro) no quede partido entre hojas
        y = espacio(doc, ctx, y, FOTO + 14 + filas.length * 8 + 22);
        if (pi > 0) { linea(doc, y - 4); y += 2; }

        // Foto a la izquierda, en un cuadro gris claro (como las miniaturas de la venta)
        const yProd = y;
        doc.setFillColor(244, 244, 246);
        doc.roundedRect(M, yProd, FOTO, FOTO, 2, 2, 'F');
        if (foto) {
            const pad = 2;
            const r = Math.min((FOTO - pad * 2) / (foto.w || FOTO), (FOTO - pad * 2) / (foto.h || FOTO));
            const w = (foto.w || FOTO) * r, h = (foto.h || FOTO) * r;
            try { doc.addImage(foto.data, foto.format, M + (FOTO - w) / 2, yProd + (FOTO - h) / 2, w, h); } catch { /* foto rota */ }
        } else {
            texto(doc, 'Sin foto', M + FOTO / 2, yProd + FOTO / 2 + 1, { tam: F.chico, color: K.rotulo, alinear: 'center' });
        }

        // Datos del producto a la derecha
        const tx = M + FOTO + 7;
        const tw = CONTENT_W - FOTO - 7;
        let ty = yProd + 3;
        rotulo(doc, multi ? `Producto ${pi + 1} de ${productos.length}` : 'Producto', tx, ty);
        ty += 6;
        doc.setFontSize(multi ? F.nombre : 14);
        const nom = doc.splitTextToSize(prod.productoNombre || '—', tw).slice(0, 2);
        texto(doc, nom, tx, ty, { tam: multi ? F.nombre : 14, negrita: true });
        ty += nom.length * (multi ? 4.6 : 5.8);
        if (prod.productoCodigo) {
            texto(doc, `SKU ${prod.productoCodigo}`, tx, ty, { tam: F.chico, color: K.rojo, negrita: true });
            ty += 5;
        }
        const desc = String(prod.productoDescripcion || '').replace(/[\r\n]+/g, ' ').trim();
        if (desc) {
            doc.setFontSize(F.texto);
            const dl = doc.splitTextToSize(desc, tw).slice(0, multi ? 2 : 4);
            texto(doc, dl, tx, ty, { tam: F.texto, color: K.gris });
            ty += dl.length * 3.9;
        }
        y = Math.max(yProd + FOTO, ty) + 6;

        // Escala de precios
        const filasTabla = filas.map(f => [
            f.cant === 1 ? '1 unidad' : `Desde ${f.cant.toLocaleString('es-AR')} unidades`,
            plata(f.unit),
            plata(f.cant * f.unit),
        ]);
        y = tabla(doc, ctx, y,
            [{ titulo: 'Cantidad' }, { titulo: 'Precio por unidad', ancho: 42, derecha: true }, { titulo: 'Total', ancho: 42, derecha: true }],
            filasTabla, { negritaCol: 1 });

        // Mejor precio: el mismo recuadro que el total de los otros PDF
        const base = filas[0];
        const mejor = filas[filas.length - 1];
        if (filas.length >= 2 && mejor.unit < base.unit) {
            const alto = 12;
            y = espacio(doc, ctx, y + 4, alto + 4);
            doc.setFillColor(244, 244, 246);
            doc.roundedRect(M, y, CONTENT_W, alto, 2, 2, 'F');
            doc.setFillColor(...K.rojo);
            doc.rect(M, y, 1.2, alto, 'F');
            texto(doc, `Mejor precio · desde ${mejor.cant.toLocaleString('es-AR')} unidades`, M + 5, y + 5.4, { tam: F.nombre, negrita: true });
            texto(doc, `${plata(base.unit - mejor.unit)} menos por unidad que la compra unitaria`, M + 5, y + 9.4, { tam: F.chico, color: K.gris });
            texto(doc, `${plata(mejor.unit)} /u`, pageW - M - 4, y + 7.8, { tam: 15, negrita: true, alinear: 'right' });
            y += alto;
        }
        y += 10;
    }

    // Notas largas: debajo, como párrafo
    if (notasCortas && !notasEnColumna) y = parrafo(doc, ctx, y, 'Condiciones / notas', notasCortas, { maxRenglones: 8 });

    // ── Pie en todas las hojas ──────────────────────────────────────────────
    const total = doc.internal.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
        doc.setPage(p);
        dibujarFooter(doc, {
            pagina: p, totalPaginas: total,
            textoCentral: 'Precios en pesos argentinos · IVA no incluido salvo indicación',
        });
    }

    const nombre = (clienteNombre || 'cliente').toLowerCase().replace(/\s+/g, '-');
    guardarPDF(doc, `cotizacion-${nombre}.pdf`);
}
